import type { ReactNode } from "react"
import { Auth0Context } from "@auth0/auth0-react"
import { mockUserId } from "~/utils/mockAuth"

interface TestAuth0ProviderProps {
  children: ReactNode
}

// Client-side org gates (`useIsCarharttWip`, `useIsLesDeux`) read `org_name`. In test mode the
// server resolves the org from `selectedOrgId` (TrpcProvider's `x-test-org-id` header), so the
// mocked user has to carry the same value or every org-gated feature silently disappears.
// Matches `testUserSession`'s default in server/api/trpc.ts when nothing is selected.
// A manual no-auth dev run lands on a company with local data instead of the empty `test_org`.
// Playwright (`navigator.webdriver`) is skipped: e2e fixtures pick their own org.
if (
  import.meta.env.VITE_IS_E2E_TESTING === "true" &&
  !navigator.webdriver &&
  !localStorage.getItem("selectedOrgId")
) {
  localStorage.setItem("selectedOrgId", "knowledgecotton_apparel")
}
const testOrgName = localStorage.getItem("selectedOrgId") ?? "test_org"

// Mocked Auth provider for e2e tests
export function TestAuth0Provider({ children }: TestAuth0ProviderProps) {
  const mockAuth0Context = {
    isAuthenticated: true,
    isLoading: false,
    user: {
      // Must match the server's mock session (x-test-user-id) — a mismatched sub breaks
      // personal-view upserts and the self-filter on live updates.
      sub: mockUserId(),
      name: "Test User",
      email: "testuser@hakio.com",
      email_verified: true,
      // TrpcProvider blocks authenticated users without an organization (#4575).
      org_id: "org_test",
      org_name: testOrgName,
      "https://app.hakio.com/roles": ["admin", "hakio"],
    },
    loginWithRedirect: async () => {
      console.warn("TestAuth0Provider: loginWithRedirect called in test mode")
    },
    logout: () => {
      console.warn("TestAuth0Provider: logout called in test mode")
    },
    getAccessTokenSilently: async () => "test-token",
    getAccessTokenWithPopup: async () => "test-token",
    getIdTokenClaims: async () => ({
      __raw: "test-token",
      name: "Test User",
      email: "testuser@hakio.com",
      email_verified: true,
      sub: mockUserId(),
      org_id: "org_test",
    }),
    loginWithPopup: async () => {
      console.warn("TestAuth0Provider: loginWithPopup called in test mode")
    },
    handleRedirectCallback: async () => ({
      appState: {},
    }),
  }

  return <Auth0Context.Provider value={mockAuth0Context as any}>{children}</Auth0Context.Provider>
}
